import { cn } from "@/utils/cn";
import { IconSelector, IconX } from "@tabler/icons-react";
import clsx from "clsx";
import debounce from "lodash/debounce";
import { useEffect, useState } from "react";
import { type ClearIndicatorProps, type DropdownIndicatorProps, type OptionProps, type SingleValue, components } from "react-select";
import AsyncSelect from "react-select/async"; // Import AsyncSelect from 'react-select/async'

type ComboBoxOption = {
	value?: string | number | null;
	label?: string | number | null;
};

type ComboboxProps<T> = {
	placeholder?: string;
	size?: "default" | "sm" | "md" | "lg";
	className?: string;
	debounceMs?: number;
	disabled?: boolean;
	placeholderValue?: ComboBoxOption;
	clearable?: boolean;
	loadOptions: (inputValue: string) => Promise<T[]>;
	minHeight?: number;
	maxHeight?: number;
	defaultOptions?: boolean;
	onValueChange?: (value: ComboBoxOption | ComboBoxOption["value"]) => void;
	returnFullOption?: boolean;
	defaultValue?: ComboBoxOption["value"];
	itemComponent?: any;
};

const ClearIndicator = (props: ClearIndicatorProps<ComboBoxOption, false>) => (
	<components.ClearIndicator {...props}>
		<IconX size={18} stroke={1.5} />
	</components.ClearIndicator>
);

const DropdownIndicator = (props: DropdownIndicatorProps<ComboBoxOption, false>) => (
	<components.DropdownIndicator {...props}>
		<IconSelector size={18} stroke={1.5} />
	</components.DropdownIndicator>
);

const Option = (props: OptionProps<ComboBoxOption, false>) => <components.Option {...props} />;

export default function Combobox<T>({
	placeholder,
	size = "default",
	debounceMs = 500,
	clearable = true,
	minHeight = 300,
	maxHeight = 400,
	defaultOptions = true,
	returnFullOption = false,
	...props
}: ComboboxProps<T>) {
	const [selectedOption, setSelectedOption] = useState<ComboBoxOption | null>(
		props.defaultValue ? { value: props.defaultValue, label: props.placeholderValue?.label } : null,
	);
	//   const [selectedOption, setSelectedOption] = useState<
	//   PropsValue<T> | undefined
	// >((props.placeholderValue as unknown) as PropsValue<T>);
	const loadOptionsDebounced = debounce(props.loadOptions, debounceMs);

	const comboBoxOptions = {
		defaultOptions,
		isClearable: clearable,
		isDisabled: props.disabled,
		dropdownIndicator: "yes",
		menuPlacement: "auto",
		minMenuHeight: minHeight,
		maxMenuHeight: maxHeight,
		onChange: (option: SingleValue<ComboBoxOption>) => {
			setSelectedOption(option);

			if (returnFullOption) {
				props.onValueChange(option);
			} else {
				props.onValueChange(option?.value || null);
			}
		},
	};

	useEffect(() => {
		if (!props.defaultValue) {
			setSelectedOption(null);
		}
	}, [props.defaultValue]);

	const handleLoadOptions = debounce((inputValue: string, callback: (options: T[]) => void) => {
		debounce(() => {
			if (props.loadOptions) {
				props
					.loadOptions(inputValue)
					.then((options) => {
						callback(options);
					})
					.catch((error) => {
						console.error(error);
						callback([]);
					});
			} else {
				callback([]);
			}
		}, debounceMs)();
	}, 500);

	return (
		<AsyncSelect
			key={props.defaultValue}
			components={{ ClearIndicator, DropdownIndicator, Option }}
			placeholder={placeholder}
			loadOptions={handleLoadOptions}
			value={selectedOption}
			className={clsx("w-full text-base", props.className)}
			classNames={{
				control: ({ isDisabled }) =>
					cn(
						"flex flex-row w-full  pl-3 !pr-0 gap-3 ",
						size === "default" && "!min-h-[38px] max-h-[38px] h-[38px] text-base !rounded-md",
						size === "sm" && "!min-h-[28px] max-h-[28px] h-[28px] px-2 text-sm rounded-md",
						size === "md" && "!min-h-[38px] max-h-[38px] h-[38px] text-base !rounded-md",
						size === "lg" && "!min-h-[48px] max-h-[48px] h-[48px] px-2 text-lg  rounded-lg",
						isDisabled
							? "!bg-muted !border-border !text-muted-foreground !cursor-not-allowed"
							: "!bg-background !border-border focus-within:!border-focus-border focus-within:!ring-2 focus-within:!ring-focus-ring/30 !ring-offset-0 !outline-0",
					),
				valueContainer: () => "!p-0",
				placeholder: () => "!text-muted-foreground",
				singleValue: ({ isDisabled }) => clsx(isDisabled ? "!text-muted-foreground" : "!text-foreground"),
				menu: () => "!mt-1 !bg-background !border-1 !border-border !rounded-md !z-[90]",
				option: ({ isFocused, isSelected }) =>
					cn(
						"!text-foreground !cursor-pointer",
						size === "default" && "!px-3 !py-2 !text-base",
						size === "sm" && "!px-2.5 !py-1.5 !text-sm",
						size === "md" && "!px-3 !py-2 !text-base",
						size === "lg" && "!px-3 !py-2 !text-lg",
						isSelected ? "!bg-blue-100 !text-foreground" : isFocused ? "!bg-blue-50" : "!text-foreground",
					),
				dropdownIndicator: () => "inline-flex items-center h-full !py-0 !text-muted-foreground",
				clearIndicator: () => "inline-flex items-center h-full !py-0 !text-muted-foreground !cursor-pointer",
			}}
			{...comboBoxOptions}
		/>
	);
}
